// Event Bus — Isomorphism Phase 2 · Blind Spot 2 (async communication formalization)
//
// Typed publish/subscribe for v2 frontend components. Replaces direct function
// calls and shared-mutable-state coupling with decoupled event-driven communication.
//
// Design decisions:
//   - No third-party library (mitt / nanoevents) — generic TS is enough for
//     our event volume (~100 events/min max in study session).
//   - Type-safe: compile-time check that event names and payload shapes match
//     the AppEventMap declaration below.
//   - Returns unsubscribe function from .on() — React components call it in
//     useEffect cleanup. No leak risk.
//   - .emit() is synchronous — events fire immediately. For async side effects,
//     the handler wraps its own async logic.
//   - Singleton pattern via createEventBus() — one instance per app lifetime,
//     shared via React context (not exported as global).
//
// Session 33: removed 3 dead events (ingest:progress, item:flagged, item:edited)
// that had zero emit() or .on() calls in the codebase. Fire-and-forget events
// (emitted but no active .on() consumer) are retained with annotations — they
// serve as integration points for future notification/analytics consumers.
//
// Reference: tech-decisions.md §1 Layer 4 "Event bus (组件间通信，禁止直接调用)"

/** All application events with their payload types.
 *  Add new events here — compile-time safety propagates to all callers. */
export interface AppEventMap {
  // ── Active: both emit() and .on() consumers ───────────────────────────
  /** Quiz round finished — record stats.  emit: use-quiz-session · on: course-dashboard */
  'quiz:completed': { courseId: string; chapterId: string; score: number; total: number };
  /** Flashcard reviewed (SRS rating submitted).  emit: flashcards-view · on: course-dashboard */
  'flashcard:reviewed': { courseId: string; itemId: string; rating: number };
  /** A new notification was pushed to the store.  emit: use-notify · on: use-notification-toast */
  'notification:new': { id: string; level: string; title: string; message?: string; persistent: boolean; duration?: number; action?: { label: string; to?: string; onClick?: () => void } };

  // ── Fire-and-forget: emit() but no active .on() consumer ──────────────
  // These serve as integration points for future analytics/notification
  // consumers. The emit sites already produce useful payloads; the .on()
  // side just hasn't been built yet.
  /** Ingest run completed (success or failure).  emit: use-regen-flow, use-regen-history, outline-view, regen-dialog, regen-view */
  'ingest:completed': { courseId: string; success: boolean; itemCount: number };
  /** Regen workflow started — polling begins.  emit: course-shell */
  'regen:started': { courseId: string; stage: string; chapter?: string; mode: string };
  /** Regen workflow completed successfully.  emit: course-shell */
  'regen:completed': { courseId: string; stage: string; itemCount: number; durationMs: number };
  /** Regen workflow failed.  emit: course-shell */
  'regen:failed': { courseId: string; stage: string; error: string };
}

type EventKey = keyof AppEventMap;
type EventHandler<K extends EventKey> = (payload: AppEventMap[K]) => void;

export interface IEventBus {
  /** Subscribe to an event. Returns unsubscribe function. */
  on<K extends EventKey>(event: K, handler: EventHandler<K>): () => void;
  /** Emit an event to all subscribers. */
  emit<K extends EventKey>(event: K, payload: AppEventMap[K]): void;
  /** Remove all subscribers. Used for test cleanup. */
  clear(): void;
}

/** Create a new EventBus instance. One per app lifetime. */
export function createEventBus(): IEventBus {
  const listeners = new Map<EventKey, Set<EventHandler<never>>>();

  return {
    on<K extends EventKey>(event: K, handler: EventHandler<K>): () => void {
      let set = listeners.get(event);
      if (!set) {
        set = new Set();
        listeners.set(event, set);
      }
      set.add(handler as EventHandler<never>);
      return () => {
        set!.delete(handler as EventHandler<never>);
      };
    },

    emit<K extends EventKey>(event: K, payload: AppEventMap[K]): void {
      const set = listeners.get(event);
      if (!set) return;
      for (const handler of set) {
        (handler as EventHandler<K>)(payload);
      }
    },

    clear(): void {
      listeners.clear();
    },
  };
}
